<template>
  <div class="ovg-page">
    <div class="ovg-page__bg" aria-hidden="true" />

    <header class="ovg-header">
      <div class="ovg-header__logos">
        <img
          src="@/assets/logo_original.svg"
          alt="Samtek"
          class="ovg-header__logo-samtek"
        />
        <img
          src="@/assets/logo_asus.png"
          alt="ASUS"
          class="ovg-header__logo-asus"
        />
      </div>
      <button type="button" class="ovg-header__salir" @click="onSalir">
        Salir <font-awesome-icon icon="sign-out-alt" />
      </button>
    </header>

    <div class="ovg-subheader">
      <div class="ovg-subheader__inner ovg-subheader__inner--wide">
        <h2 class="ovg-subheader__title">
          Registra tu equipo para envío a servicio técnico
        </h2>
        <p class="ovg-subheader__subtitle">
          Completa los datos de tu equipo por adelantado. Al finalizar,
          recibirás las instrucciones exactas para embalar y despachar tu equipo
          de forma segura desde tu región.
        </p>
      </div>
    </div>

    <div class="ovg-content ovg-content--wide">
      <div class="ovg-card">
        <div class="ovg-progress">
          <div class="ovg-progress__track">
            <div class="ovg-progress__fill" :style="{ width: progressWidth }" />
          </div>
          <span class="ovg-progress__label">Paso Actual: {{ stepLabel }}</span>
        </div>

        <div class="ovg-steps">
          <div
            v-for="(label, n) in stepLabels"
            :key="n"
            class="ovg-step"
            :class="stepClass(Number(n))"
          >
            <span class="ovg-step__num">{{ n }}</span>
            <span class="ovg-step__label">{{ label }}</span>
          </div>
        </div>

        <DataClientRegions
          v-if="step === STEP_CLIENTE"
          :returnClient="formClient.datosProvisorios"
          :setDatosProvisorios="setDataClient"
        />
        <DataEquipmentRegions
          v-if="step === STEP_EQUIPO"
          :returnEquipment="formEquipment"
          :setFormEquipment="setDataEquipment"
          @goBack="step = STEP_CLIENTE"
        />
        <WarrantyFailsRegions
          v-if="step === STEP_FALLA"
          :warrantyReturn="formClient.formWarranty"
          :setDataWarranty="setDataWarrantyFails"
          :boleta="boleta"
          :loading="cargandoConsentimiento"
          @goBack="step = STEP_EQUIPO"
        />
        <ConsentRegions
          v-if="step === STEP_CONSENTIMIENTO"
          :documentUrl="consentimiento.url"
          :firma="firma"
          :setFirma="setFirma"
          @goBack="step = STEP_FALLA"
          @next="step = STEP_RESUMEN"
        />
        <ResumeRegions
          v-if="step === STEP_RESUMEN"
          :dataClient="formClient.datosProvisorios"
          :warrantyFail="formClient.formWarranty"
          :dataEquipment="formEquipment"
          :boleta="boleta"
          :firma="consentimiento.url ? firma : null"
          @goBack="volverDesdeResumen"
          @finish="cancel"
        />
      </div>
    </div>

    <!-- Botón de contacto: oculto hasta que el negocio lo habilite (mismo criterio que OVGStore) -->

    <p class="ovg-page__footer">
      © 2026 Samtek | Servicio Técnico ASUS Autorizado en Chile.
    </p>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import settings from '../../../../settings'
import DataClientRegions from './FormRegiones/DataClientRegions.vue'
import DataEquipmentRegions from './FormRegiones/DataEquipmentRegions.vue'
import WarrantyFailsRegions from './FormRegiones/WarrantyFailsRegions.vue'
import ConsentRegions from './FormRegiones/ConsentRegions.vue'
import ResumeRegions from './FormRegiones/ResumeRegions.vue'
import { CLAVE_CONSENTIMIENTO_POR_TIPO_SERVICIO } from './FormRegiones/consentimiento'

const STEP_CLIENTE = 1
const STEP_EQUIPO = 2
const STEP_FALLA = 3
const STEP_CONSENTIMIENTO = 4
const STEP_RESUMEN = 5

const STEP_LABELS = {
  [STEP_CLIENTE]: 'Tus Datos',
  [STEP_EQUIPO]: 'Datos del Equipo',
  [STEP_FALLA]: 'Detalle y Falla',
  [STEP_CONSENTIMIENTO]: 'Consentimiento',
  [STEP_RESUMEN]: 'Resumen',
}

const estadoInicial = () => ({
  formClient: {
    datosProvisorios: {
      rut: null,
      nombre: null,
      apellido: null,
      email: null,
      telefono: null,
      direccion: null,
      comuna: null,
      region: null,
    },
    formWarranty: {
      tipo_servicio: null,
      reporte_falla_cliente: null,
      fecha_de_compra: null,
    },
  },
  formEquipment: {
    serial_number: null,
    password: null,
    size_hard_drive: '',
    power_source: '',
    total_ram_memorie: '',
    model_name: null,
    modelo: null,
  },
  boleta: '',
  consentimiento: { clave: null, url: '' },
  firma: null,
  cargandoConsentimiento: false,
  step: STEP_CLIENTE,
})

// Media URLs come back relative so they inherit the API host and protocol
const resolverUrlMedia = (url) => {
  try {
    return new URL(url, settings.baseApi).href
  } catch (e) {
    return url
  }
}

export default {
  name: 'PreIngressRegions',
  components: {
    DataClientRegions,
    DataEquipmentRegions,
    WarrantyFailsRegions,
    ConsentRegions,
    ResumeRegions,
  },
  data() {
    return {
      ...estadoInicial(),
      stepLabels: STEP_LABELS,
      STEP_CLIENTE,
      STEP_EQUIPO,
      STEP_FALLA,
      STEP_CONSENTIMIENTO,
      STEP_RESUMEN,
    }
  },
  computed: {
    stepLabel() {
      return STEP_LABELS[this.step] || ''
    },
    progressWidth() {
      return `${(this.step / STEP_RESUMEN) * 100}%`
    },
  },
  methods: {
    ...mapActions('PreIngreso', ['getArchivoConfigurable']),
    stepClass(n) {
      if (this.step === n) return 'ovg-step--active'
      if (this.step > n) return 'ovg-step--done'
      return ''
    },
    setDataClient(datosProvisorios) {
      this.formClient.datosProvisorios = datosProvisorios
      this.step = STEP_EQUIPO
    },
    setDataEquipment(formEquipment) {
      this.formEquipment = formEquipment
      this.step = STEP_FALLA
    },
    async setDataWarrantyFails(formWarranty, boleta) {
      this.formClient.formWarranty = formWarranty
      this.boleta = boleta

      const clave =
        CLAVE_CONSENTIMIENTO_POR_TIPO_SERVICIO[formWarranty.tipo_servicio] ||
        null
      if (clave !== this.consentimiento.clave) {
        this.firma = null
      }
      if (!clave) {
        this.consentimiento = { clave: null, url: '' }
        this.step = STEP_RESUMEN
        return
      }

      this.cargandoConsentimiento = true
      try {
        const { data } = await this.getArchivoConfigurable(clave)
        const url = data && data.url ? resolverUrlMedia(data.url) : ''
        if (url !== this.consentimiento.url) {
          this.firma = null
        }
        this.consentimiento = { clave, url }
        this.step = url ? STEP_CONSENTIMIENTO : STEP_RESUMEN
      } catch (error) {
        this.$bvToast.toast(
          'No pudimos cargar el documento de consentimiento. Intenta nuevamente.',
          { variant: 'danger' }
        )
      } finally {
        this.cargandoConsentimiento = false
      }
    },
    setFirma(firma) {
      this.firma = firma
    },
    volverDesdeResumen() {
      this.step = this.consentimiento.url ? STEP_CONSENTIMIENTO : STEP_FALLA
    },
    cancel() {
      Object.assign(this.$data, estadoInicial())
    },
    async onSalir() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        '¿Estás seguro que deseas cancelar? Se perderán los datos ingresados',
        {
          title: 'Cancelar pre-ingreso',
          okVariant: 'warning',
          okTitle: 'Sí, cancelar',
          cancelTitle: 'Volver',
          centered: true,
        }
      )
      if (confirmed) {
        this.cancel()
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@/style/portal-cliente.scss';
</style>
